import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, useRoutes } from 'react-router-dom';
// layouts
import MenuLayout from '../layouts/menu';
import AuthLayout from '../layouts/AuthLayout';
import AppLayout from '../layouts/AppLayout';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';

// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// components
import LoadingScreen from '../components/common/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
};

// IMPORT COMPONENTS
const TermsAndPrivacy = Loadable(lazy(() => import('../pages/TermsAndPrivacy')));

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const TikTokCallback = Loadable(lazy(() => import('../pages/authentication/TikTokCallback')));

const CreateNewAccount = Loadable(lazy(() => import('../pages/authentication/CreateNewAccount')));
const ForgotPassword = Loadable(lazy(() => import('../pages/authentication/ForgotPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));

const Home = Loadable(lazy(() => import('../pages/home/Home')));
const Cart = Loadable(lazy(() => import('../pages/cart/Cart')));

const Search = Loadable(lazy(() => import('../pages/search/Search')));
const ProductView = Loadable(lazy(() => import('../pages/product/ProductView')));

const Me = Loadable(lazy(() => import('../pages/general/Me')));
const Theme = Loadable(lazy(() => import('../pages/general/Theme')));
const Language = Loadable(lazy(() => import('../pages/general/Language')));
const Address = Loadable(lazy(() => import('../pages/general/Address')));
const NewAddress = Loadable(lazy(() => import('../pages/general/NewAddress')));
const EditAddress = Loadable(lazy(() => import('../pages/general/EditAddress')));
const MyAccount = Loadable(lazy(() => import('../pages/general/MyAccount')));

const CreateOrder = Loadable(lazy(() => import('../pages/order/CreateOrder')));
const SelectAddress = Loadable(lazy(() => import('../pages/order/SelectAddress')));
const AddOrderPayment = Loadable(lazy(() => import('../pages/order/AddOrderPayment')));
const OrderList = Loadable(lazy(() => import('../pages/order/OrderList')));
const ViewOrder = Loadable(lazy(() => import('../pages/order/ViewOrder')));

// Error
const Maintenance = Loadable(lazy(() => import('../pages/error/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/error/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/error/Page404')));

const routes = [
  // Authentication Routes
  {
    path: 'auth',
    element: (
      <GuestGuard>
        <AuthLayout />
      </GuestGuard>
    ),
    children: [
      { path: 'login', element: <Login /> },
      { path: 'tiktok/callback', element: <TikTokCallback /> },
      { path: 'create-new-account', element: <CreateNewAccount /> },
    ],
  },
  // Public Routes
  {
    path: '',
    element: <MenuLayout />,
    children: [
      { element: <Navigate to={'home'} replace />, index: true },
      { path: 'home', element: <Home /> },
      { path: 'cart', element: <Cart /> },
      {
        path: 'me',
        element: (
          <AuthGuard>
            <Me />
          </AuthGuard>
        ),
      },
    ],
  },

  {
    path: 'app',
    element: <AppLayout />,
    children: [
      { path: 'search', element: <Search /> },
      { path: 'product/:slug', element: <ProductView /> },
      { path: 'order/create-new', element: <CreateOrder /> },
    ],
  },

  {
    path: 'order',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    children: [
      { path: 'create-new', element: <CreateOrder /> },
      { path: 'select-address', element: <SelectAddress /> },
      { path: 'add-payment', element: <AddOrderPayment /> },
      { path: 'list', element: <OrderList /> },
      { path: 'view', element: <ViewOrder /> },
    ],
  },

  {
    path: 'general',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    children: [
      { path: 'my-account', element: <MyAccount /> },
      { path: 'theme', element: <Theme /> },
      { path: 'address', element: <Address /> },
      { path: 'address/new-address', element: <NewAddress /> },
      { path: 'address/edit-address/:id', element: <EditAddress /> },
      { path: 'language', element: <Language /> },
    ],
  },
  {
    path: 'auth',
    element: <AuthLayout />,
    children: [
      { path: 'forgot-password', element: <ForgotPassword /> },
      { path: 'verify', element: <VerifyCode /> },
      { path: 'reset-password', element: <ResetPassword /> },
    ],
  },
  // Dashboard Routes
  // {
  //   path: '',
  //   element: (
  //     <AuthGuard>
  //       <MenuLayout />
  //     </AuthGuard>
  //   ),
  //   children: [
  //     {
  //       path: 'settings',
  //       children: [
  //         { element: <Navigate to="my-account" replace />, index: true },
  //         { path: 'my-account', element: <MyAccount /> },
  //       ],
  //     },
  //   ],
  // },
  // Errors Routes
  {
    path: '*',
    element: <LogoOnlyLayout />,
    children: [
      { path: 'terms-and-policy', element: <TermsAndPrivacy /> },
      { path: 'maintenance', element: <Maintenance /> },
      { path: '500', element: <Page500 /> },
      { path: '404', element: <NotFound /> },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
];

const Router = createBrowserRouter(routes);

export default Router;
